import { StringTMap, ValueOf } from '@@types/common';

import { MINUTE } from '~/constants/date';
import logging from '~/stores/providers/logging';
import { memoize } from 'lodash';

export const EVENT_NAME = {
  FOCUS: 'focus',
  MESSAGE: 'message',
  RESIZE: 'resize',
  CLICK: 'click',
  ERROR: 'error',
  UNHANDLED_REJECTION: 'unhandledrejection',
  KEYDOWN: 'keydown',
  MOUSEDOWN: 'mousedown',
  MOUSEUP: 'mouseup',
  MOUSEMOVE: 'mousemove',
  ONLINE: 'online',
  OFFLINE: 'offline',
  EVERY_MINUTE: 'everyMinute',
} as const;

export type EVENT_NAME = ValueOf<typeof EVENT_NAME>;

export type Listener = (event: Event) => void;

export class GlobalEventService {
  public listenerMap: StringTMap<Listener[]> = {};

  constructor() {
    window.setInterval(() => {
      this.triggerEvent(EVENT_NAME.EVERY_MINUTE);
    }, MINUTE);
  }

  public addEventListener = (
    eventName: EVENT_NAME,
    listener: Listener,
    options?: boolean | AddEventListenerOptions
  ) => {
    if (this.findListenerIndex(eventName, listener) === -1) {
      this.listenerMap[eventName].push(listener);
      this.attachEvent(eventName, options);
    }
    return () => this.removeEventListener(eventName, listener);
  };

  private removeEventListener = (eventName: EVENT_NAME, listener: Listener) => {
    const index = this.findListenerIndex(eventName, listener);
    if (index > -1) {
      this.listenerMap[eventName].splice(index, 1);
      this.detachEvent(eventName);
    } else {
      logging.trace('eventListener not found');
    }
  };

  public triggerEvent = (eventName: EVENT_NAME, event?: Event) => {
    this.listenerMap[eventName]?.forEach((fn) => fn(event));
  };

  private attachEvent = (eventName: EVENT_NAME, options?: boolean | AddEventListenerOptions) => {
    if (this.listenerMap[eventName].length === 1) {
      window.addEventListener(eventName, this.listenEvent(eventName), options);
    }
  };

  private detachEvent = (eventName: EVENT_NAME) => {
    if (this.listenerMap[eventName].length === 0) {
      window.removeEventListener(eventName, this.listenEvent(eventName));
    }
  };

  private listenEvent = memoize((eventName: EVENT_NAME) => {
    return (event: Event) => {
      this.triggerEvent(eventName, event);
    };
  });

  private findListenerIndex = (eventName: EVENT_NAME, listener: Listener) => {
    this.listenerMap[eventName] ??= [];
    return this.listenerMap[eventName].indexOf(listener);
  };
}
const globalEventService = new GlobalEventService();

export default globalEventService;
